import React, { useMemo, useState } from "react";
import { SearchIcon, TypingIcon } from "@omega/icons";
import {
    Button,
    CompactOutlinedInput,
    Icon,
    InputAdornment,
    OutlinedInput,
    SearchableSelectField,
    Spinner,
    StatePlaceholder,
    type CollectionKey,
    type CollectionState,
    type PickerItemRenderContext,
    type PickerOverlaySurface,
    type PickerOverlaySurfaceContent,
    type PickerSearchStateActionSlot,
    type PickerVirtualization,
} from "@omega/ui";
import { createFieldPickerFullscreenRenderer, type FieldPickerFullscreenClose } from "@app/omega-ui/fieldPickerFullscreen";
import type { PickerOverlayMode } from "../overlay/picker-overlay";
import type { PickerListItemKey, PickerListVirtualizationConfig } from "../picker-list";
import { resolveOptionSurfaceRender, type OptionSurfaceRenderContext, type OptionSurfaceSlots } from "../picker-option";
import "./SearchSelect.scss";

export type NullableSelectedValue<T> = T | null;
export type SearchSelectOverlayMode = PickerOverlayMode;

/** Контекст render опции {@link SearchSelect}. */
export type SearchSelectOptionRenderContext = OptionSurfaceRenderContext;

/** Per-surface overrides опций {@link SearchSelect}. */
export type SearchSelectOptionSlots<T> = OptionSurfaceSlots<T>;

/**
 * Density для наполнения overlay: компактный popover vs читаемый fullscreen.
 * @param surface resolved surface от {@link SearchableSelectField}
 */
export function overlayContentSize(surface: PickerOverlaySurface): "sm" | "md" {
    return surface === "fullscreen" ? "md" : "sm";
}

/** Пропсы {@link SearchSelect}. */
export interface SearchSelectProps<T> {
    className?: string;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    value: NullableSelectedValue<T>;
    options: T[] | null;
    /**
     * Первичная / blocking-загрузка списка. \
     * Показывает `slots.states.loading` вместо опций. Не путать с {@link isRefreshing}.
     */
    isLoading?: boolean;
    /**
     * Фоновое обновление уже показанного списка. \
     * Список остаётся видимым; package рисует `LinearProgress` сверху.
     * @see {@link SearchableSelectField} `isRefreshing`
     */
    isRefreshing?: boolean;
    searchPlaceholder?: string;
    /**
     * Контент idle-состояния списка (до начала поиска). \
     * Node или render-prop с `surface` — как `slots.states.idle` у {@link SearchableSelectField}.
     * @param ctx.surface resolved presentation surface
     * @see {@link overlayContentSize}
     */
    idleText?: PickerOverlaySurfaceContent;
    /**
     * Доп. действие рядом с idle (например, «Показать весь список»). \
     * Прокидывается в `slots.states.idleAction`; layout — у package.
     * @param api.query текущая строка поиска
     * @param api.clearSearch очищает поиск и возвращает фокус
     * @param api.surface resolved presentation surface
     * @see {@link overlayContentSize}
     */
    idleAction?: PickerSearchStateActionSlot;
    emptyText?: string;
    /**
     * Plain-text опции: a11y / typeahead и default content обеих surfaces. \
     * Для богатого UI задайте {@link optionSlots}, не подменяя этот текст.
     * @param option опция списка
     */
    getOptionLabel: (option: T) => string;
    /**
     * Отображение выбранного значения в trigger. \
     * @default {@link getOptionLabel}
     * @param option выбранная опция
     */
    getSelectedLabel?: (option: T) => React.ReactNode;
    /**
     * Возвращает стабильный ключ опции.
     * @param option опция списка
     * @param index индекс опции
     */
    getOptionKey?: (option: T, index: number) => PickerListItemKey;
    /**
     * Точечные overrides рендера опции по surface. \
     * Задайте только нужный surface — остальные defaults SearchSelect сохраняются. \
     * `popover` **не** копируется в fullscreen.
     * @example
     * ```tsx
     * optionSlots={{
     *   fullscreen: (item) => (
     *     <PickerOptionCard
     *       title={item.sign}
     *       details={[{ label: "Наименование", value: item.name }]}
     *     />
     *   ),
     * }}
     * ```
     */
    optionSlots?: SearchSelectOptionSlots<T>;
    onSearch?: (value: string) => void;
    onChange?: (value: NullableSelectedValue<T>) => void;
    error?: boolean;
    statusSlot?: React.ReactNode | null;
    placeholder?: string;
    /**
     * Режим поверхности выбора.
     * @default "auto"
     */
    overlayMode?: SearchSelectOverlayMode;
    /**
     * Media query, при котором `overlayMode="auto"` переключается в fullscreen.
     * @default "(max-width: 640px), (pointer: coarse)"
     */
    fullscreenMediaQuery?: string;
    /** Заголовок fullscreen picker. Если не задан, используется `label` или `placeholder`. */
    fullscreenTitle?: React.ReactNode;
    /**
     * Управляет виртуализацией списка опций.
     * @default true
     */
    pickerListVirtualization?: boolean | PickerListVirtualizationConfig;
    /**
     * Нижнее действие закрытия fullscreen overlay. \
     * По умолчанию — `FieldPickerFullscreenCloseButton` («Вернуться назад» + стрелка влево). \
     * `false` — скрыть. Render prop — полная замена (можно переиспользовать дефолтную кнопку).
     * @param api.dismiss закрывает fullscreen overlay
     * @see {@link FieldPickerFullscreenClose}
     */
    renderFullscreenClose?: FieldPickerFullscreenClose;
}

function resolveVirtualization(virtualization: boolean | PickerListVirtualizationConfig | undefined): PickerVirtualization {
    if (virtualization === false) {
        return false;
    }
    if (virtualization === true || virtualization == null) {
        // Fullscreen card row (~48) + gap padding (4); popover measure corrects down.
        return { estimateSize: 52 };
    }
    if (virtualization.enabled === false) {
        return false;
    }
    return {
        estimateSize: virtualization.estimateItemHeight,
        overscan: virtualization.overscan,
    };
}

function defaultOptionChrome(label: string, surface: PickerOverlaySurface): React.ReactNode {
    return (
        <span
            className={
                surface === "fullscreen"
                    ? "omp-search-select__option omp-search-select__option--fullscreen"
                    : "omp-search-select__option omp-search-select__option--popover"
            }
        >
            {label}
        </span>
    );
}

/**
 * Controlled search-select: facade над `@omega/ui` SearchableSelectField.
 * Floating label, NBP-like searchable placeholders, fullscreen через `OmegaUiProvider` + footer close. \
 * Опции: `getOptionLabel` (content) + optional `optionSlots` (per-surface chrome). \
 * Size placeholders / idle-action / empty-action следует `surface` (`sm` popover, `md` fullscreen). \
 * Blocking load → `isLoading` + `StatePlaceholder`; soft refetch → `isRefreshing` (package `LinearProgress`).
 */
export function SearchSelect<T extends object>({
    label,
    required = false,
    disabled = false,
    value,
    options,
    isLoading = false,
    isRefreshing = false,
    searchPlaceholder = "Поиск",
    idleText,
    idleAction,
    emptyText = "Нет результатов",
    getOptionLabel,
    getSelectedLabel,
    getOptionKey,
    optionSlots,
    onSearch,
    onChange,
    error = false,
    statusSlot,
    placeholder,
    overlayMode = "auto",
    fullscreenMediaQuery,
    fullscreenTitle,
    pickerListVirtualization,
    renderFullscreenClose,
}: SearchSelectProps<T>) {
    const [searchText, setSearchText] = useState("");
    const items = options ?? [];

    const getKey = (item: T): CollectionKey => {
        const index = items.indexOf(item);
        if (getOptionKey != null) {
            return getOptionKey(item, index >= 0 ? index : 0);
        }
        return index >= 0 ? index : 0;
    };

    const selectedKey = useMemo((): CollectionKey | null => {
        if (statusSlot != null || value == null) return null;
        if (getOptionKey != null) {
            const index = items.indexOf(value);
            return getOptionKey(value, index >= 0 ? index : 0);
        }
        const index = items.indexOf(value);
        return index >= 0 ? index : "selected";
    }, [statusSlot, value, items, getOptionKey]);

    const collectionState = useMemo((): CollectionState | undefined => {
        if (isLoading) return "loading";
        if (options == null) return "idle";
        if (options.length === 0 && (idleText != null || idleAction != null) && searchText.trim() === "") {
            return "idle";
        }
        if (options.length === 0) return "empty";
        return undefined;
    }, [isLoading, options, idleText, idleAction, searchText]);

    const fieldLabel = label ?? placeholder;
    const collectionLabel =
        typeof fullscreenTitle === "string" || typeof fullscreenTitle === "number"
            ? String(fullscreenTitle)
            : (label ?? placeholder);

    const renderFullscreen = useMemo(
        () => createFieldPickerFullscreenRenderer(renderFullscreenClose),
        [renderFullscreenClose],
    );

    const optionDefaults = useMemo(
        () => ({
            popover: (option: T, _ctx: OptionSurfaceRenderContext) => defaultOptionChrome(getOptionLabel(option), "popover"),
            fullscreen: (option: T, _ctx: OptionSurfaceRenderContext) =>
                defaultOptionChrome(getOptionLabel(option), "fullscreen"),
        }),
        [getOptionLabel],
    );

    const renderListItem = (item: T, context: PickerItemRenderContext) => {
        const optionCtx: OptionSurfaceRenderContext = {
            selected: context.selected,
            disabled: context.disabled,
            surface: context.surface,
        };
        const render = resolveOptionSurfaceRender(context.surface, optionSlots, optionDefaults);
        return render(item, optionCtx);
    };

    return (
        <SearchableSelectField
            classes={{
                overlay: "spp-ui-theme omp-omega-ui",
            }}
            clearable={statusSlot == null && value != null}
            collectionLabel={collectionLabel}
            disabled={disabled}
            fullscreenMediaQuery={fullscreenMediaQuery}
            fullWidth
            getKey={getKey}
            getTextValue={getOptionLabel}
            invalid={error}
            isRefreshing={isRefreshing}
            items={items}
            label={fieldLabel}
            labelBehavior="floating"
            mode={overlayMode}
            onClear={() => {
                onChange?.(null);
                setSearchText("");
                onSearch?.("");
            }}
            onSearchValueChange={(next) => {
                setSearchText(next);
                onSearch?.(next);
            }}
            onSelectionChange={(key) => {
                if (key == null) {
                    onChange?.(null);
                    return;
                }
                const selected = items.find((item) => getKey(item) === key) ?? null;
                onChange?.(selected);
            }}
            placeholder={statusSlot ?? placeholder}
            renderFullscreen={renderFullscreen}
            renderItem={renderListItem}
            renderValue={(item) => statusSlot ?? (getSelectedLabel ?? getOptionLabel)(item)}
            required={required}
            searchLabel="Поиск"
            searchPlaceholder={searchPlaceholder}
            searchValue={searchText}
            selectedItem={statusSlot != null ? null : value}
            selectedKey={selectedKey}
            slotProps={{
                surface: { style: { gap: 4, padding: 4 } },
            }}
            slots={{
                search: ({ inputProps, surface }) => (
                    <>
                        {overlayContentSize(surface) === "sm" ? (
                            <CompactOutlinedInput
                                {...inputProps}
                                startAdornment={
                                    <InputAdornment>
                                        <Icon icon={SearchIcon} size={16} />
                                    </InputAdornment>
                                }
                            />
                        ) : (
                            <OutlinedInput
                                {...inputProps}
                                startAdornment={
                                    <InputAdornment>
                                        <Icon icon={SearchIcon} size={16} />
                                    </InputAdornment>
                                }
                            />
                        )}
                    </>
                ),
                states: {
                    empty: ({ surface }) => (
                        <StatePlaceholder
                            size={overlayContentSize(surface)}
                            title={emptyText}
                            description="Попробуйте изменить поисковый запрос"
                            icon={<Icon icon={SearchIcon} size="100%" />}
                        />
                    ),
                    idle:
                        idleText ??
                        (({ surface }) => (
                            <StatePlaceholder
                                size={overlayContentSize(surface)}
                                title={typeof searchPlaceholder === "string" ? searchPlaceholder : "Поиск"}
                                description="Начните вводить для поиска"
                                icon={<Icon icon={TypingIcon} size="100%" />}
                            />
                        )),
                    idleAction,
                    loading: ({ surface }) => (
                        <StatePlaceholder
                            size={overlayContentSize(surface)}
                            title="Загрузка..."
                            icon={<Spinner size={overlayContentSize(surface)} />}
                        />
                    ),
                    emptyAction: ({ clearSearch, surface }) => (
                        <Button size={overlayContentSize(surface)} tone="accent" variant="ghost" onClick={clearSearch}>
                            Очистить поиск
                        </Button>
                    ),
                },
            }}
            state={collectionState}
            virtualized={resolveVirtualization(pickerListVirtualization)}
        />
    );
}
